/**
 * cache-targets — how much of its prompt each provider family should read from the cache, per
 * call site, and where the cache watch pages (`CachePolicy.targets`, read by the server's
 * alert-rules-cache-watch.ts through resolveCapabilities, never by a model-name pattern).
 *
 * `target` is the healthy ceiling a fixed prefix reaches; `pageBelow` is the share under which
 * the watch pages. Measured anchors (scratchpad cache-everywhere audit, 2026-10-04, 7 days):
 *   - later rounds: GPT-6 0.969 (staging since the memory flip), 2,244 of 2,382 prod Luna rounds
 *     at 0.97+; DeepSeek 0.943-0.946; Sonnet 0.959; Gemini 0.806-0.831, its own plateau (fast
 *     tool loops never read the request one second older, only the shared head);
 *   - sweeps after SWEEP_PREFIX_PARITY_V1: 0.93-0.97 (Gemini 0.79);
 *   - a new task warm from the person's last one: 0.94-0.95 after the memory flags on GPT-6 and
 *     DeepSeek; Anthropic reads only up to its fleet breakpoint today (~0.77); Gemini 0.84 when
 *     its best-effort cache hits, 0.69 overall.
 * A model with no targets is judged against its own 7-day band alone.
 */

export interface CacheTarget {
  /** The share a healthy lane holds. */
  target: number;
  /** The share under which the cache watch pages. */
  pageBelow: number;
}

export interface CacheTargets {
  /** Main-loop rounds from round two. */
  later: CacheTarget;
  /** A new task's first round, warm from the same person's last task on the same model. */
  firstWarm: CacheTarget;
  /** The off-loop memory sweep (it sends the loop's prefix under SWEEP_PREFIX_PARITY_V1). */
  sweep: CacheTarget;
}

export const ANTHROPIC_CACHE_TARGETS: CacheTargets = {
  later: { target: 0.95, pageBelow: 0.85 },
  firstWarm: { target: 0.75, pageBelow: 0.55 },
  sweep: { target: 0.92, pageBelow: 0.8 },
};

/** GPT-5.6+ (reads at lookup boundaries); assumes RESPONSES_MEMORY_INPUT_V1 for the warm first round. */
export const OPENAI_BOUNDARY_CACHE_TARGETS: CacheTargets = {
  later: { target: 0.95, pageBelow: 0.85 },
  firstWarm: { target: 0.92, pageBelow: 0.75 },
  sweep: { target: 0.93, pageBelow: 0.8 },
};

/** DeepSeek on Fireworks (replica-pinned by x-session-affinity); assumes CHAT_TAIL_AFTER_TOOLS_V1. */
export const FIREWORKS_CACHE_TARGETS: CacheTargets = {
  later: { target: 0.94, pageBelow: 0.85 },
  firstWarm: { target: 0.92, pageBelow: 0.75 },
  sweep: { target: 0.92, pageBelow: 0.8 },
};

/** Gemini's implicit cache is best effort: 18% of new tasks read nothing, fast rounds plateau at ~0.81. */
export const GEMINI_CACHE_TARGETS: CacheTargets = {
  later: { target: 0.82, pageBelow: 0.7 },
  firstWarm: { target: 0.7, pageBelow: 0.5 },
  sweep: { target: 0.79, pageBelow: 0.65 },
};

/** Assumes `x-grok-conv-id` routing (cache-everywhere fix 10): without it a new task reads ~0 on a random server. */
export const XAI_CACHE_TARGETS: CacheTargets = {
  later: { target: 0.95, pageBelow: 0.8 },
  firstWarm: { target: 0.85, pageBelow: 0.6 },
  sweep: { target: 0.9, pageBelow: 0.7 },
};
